import { Product } from "./types";

export function getPageNumberFromName(name: string) {
  const match = name.match(/Page\s+(\d+)/i);
  const page = match ? Number(match[1]) : 1;
  return Number.isNaN(page) || page <= 0 ? 1 : page;
}

export function hasRowDiff(headers: string[], rowValues: any[], products: Product[]) {
  const skuHeaderIndex = headers.findIndex((header) => normalizeHeader(header) === "sku");
  if (skuHeaderIndex === -1) return false;

  const skuValue = toComparable(rowValues[skuHeaderIndex]);
  if (!skuValue) return true;

  const matchingProduct = products.find((product) => toComparable(product?.sku) === skuValue);
  if (!matchingProduct) return true;

  return headers.some((header, index) => {
    const key = normalizeHeader(header);
    if (!key) return false;
    const productKey = mapHeaderToProductKey(key);
    const sheetValue = toComparable(rowValues[index]);
    const productValue = toComparable(matchingProduct?.[productKey]);
    return sheetValue !== productValue;
  });
}

export function buildMagentoPayload(headers: string[], rowValues: any[]) {
  return headers.reduce<Record<string, any>>((acc, header, index) => {
    const key = normalizeHeader(header);
    if (!key) return acc;
    const productKey = mapHeaderToProductKey(key);
    const value = normalizeValue(productKey, rowValues[index]);
    acc[productKey] = value;
    return acc;
  }, {});
}

export function extractProductFromResponse(response: any, fallback: Record<string, any>, sku?: string) {
  if (response?.product) return response.product;
  if (response?.sku) return response;
  if (sku || fallback) {
    return { ...fallback, sku: sku || fallback.sku };
  }
  return null;
}

export function upsertProductInList(products: Product[], nextProduct: Product) {
  const nextSku = toComparable(nextProduct?.sku);
  if (!nextSku) return products;

  const index = products.findIndex((product) => toComparable(product?.sku) === nextSku);
  if (index === -1) return [...products, nextProduct];

  const updated = [...products];
  updated[index] = { ...updated[index], ...nextProduct };
  return updated;
}

export function normalizeHeader(header: string) {
  return header ? header.toString().trim().toLowerCase() : "";
}

export function normalizeValue(productKey: string, value: any) {
  const numericKeys = new Set(["price", "attribute_set_id", "status", "visibility", "quantity", "weight"]);
  if (!numericKeys.has(productKey)) return value;
  if (value === null || value === undefined || value === "") return value;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
}

export function mapHeaderToProductKey(normalizedHeader: string) {
  if (normalizedHeader === "type") return "type_id";
  return normalizedHeader.replace(/\s+/g, "_");
}

export function toComparable(value: any) {
  if (value === null || value === undefined) return "";
  return value.toString().trim();
}
